/**
 * src/shared/lib/conflictResolver.ts
 *
 * Pure room-level conflict resolver — client half of the no-loss guarantee (CLOUD-04).
 *
 * When the server returns a merged ProjectSnapshot after applying LWW, this function
 * reconstructs the final local state by appending any local-only rooms (rooms that
 * exist locally but were never synced to the server — added offline).
 *
 * WORKER-SAFE: No React imports, no DOM APIs, no side effects.
 * Can be used in browser, Worker, and test environments identically.
 *
 * See RESEARCH §Pattern 4 for the design rationale.
 */

import type { z } from 'zod';
import type { ProjectSnapshotSchema } from '@/shared/validation/schemas';

type ProjectSnapshot = z.infer<typeof ProjectSnapshotSchema>;

/**
 * Merge the server's authoritative ProjectSnapshot list with the local state.
 *
 * Algorithm:
 *   - For each server project: take it as authoritative (LWW already applied on server)
 *   - Append any local-only rooms: rooms present in the local project but ABSENT from
 *     the server response. These are rooms added offline after the last sync, unknown
 *     to the server.
 *   - If there is no local project for a server project: take server as-is.
 *   - Projects present only locally (not in serverMerged): NOT included in output
 *     (caller is responsible for handling local-only projects if needed).
 *
 * @param localProjects Map of projectId → local ProjectSnapshot (current local state)
 * @param serverMerged  Array of ProjectSnapshots returned by the sync/batch endpoint
 * @returns             Merged ProjectSnapshot[] with local-only rooms preserved
 */
export function mergeProjectsFromServer(
  localProjects: Map<string, ProjectSnapshot>,
  serverMerged: ProjectSnapshot[],
): ProjectSnapshot[] {
  return serverMerged.map((serverProject) => {
    const projectId = String(serverProject.id);
    const local = localProjects.get(projectId);

    // No local version — server project is taken as-is (new from server)
    if (!local) {
      return serverProject;
    }

    // Server is authoritative (LWW already applied for shared rooms).
    // Identify rooms that exist locally but are absent from the server response.
    // These are rooms added offline and never synced — must be preserved.
    const serverRoomIds = new Set(serverProject.rooms.map((r) => r.id));
    const localOnlyRooms = local.rooms.filter((r) => !serverRoomIds.has(r.id));

    return {
      ...serverProject,
      rooms: [...serverProject.rooms, ...localOnlyRooms],
    };
  });
}
